import { useState } from "react";
import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { getVideoDetails, toBackendUrl } from "@/domains/certifications/utils";
import { VideoCardProps } from "@/features/certifications/module/components/types";

import "./styles.css";

const VideoCard = ({
  number,
  video,
  progress,
  isWatched,
  onPlay,
}: VideoCardProps) => {
  const { t } = useTranslation();
  const details = getVideoDetails(video);
  const [hasThumbnailFailed, setHasThumbnailFailed] = useState(false);
  const coveredPercent = Math.min(
    Number(progress?.video?.coveredPercent ?? 0),
    100,
  );
  const minutes = Math.round((details?.durationSeconds ?? 0) / 60);

  let status = t("certification.module.videos.notStarted");
  if (isWatched) {
    status = t("certification.module.videos.watched");
  } else if (coveredPercent > 0) {
    status = t("certification.module.videos.inProgress");
  }
  if (minutes > 0) {
    status = t("certification.module.videos.withMinutes", { status, minutes });
  }

  return (
    <article className="module-video flex flex-col gap-200">
      <div className="module-video-still">
        {details?.thumbnailUrl && !hasThumbnailFailed && (
          <img
            src={toBackendUrl(details.thumbnailUrl)}
            alt=""
            loading="lazy"
            className="module-video-thumbnail"
            onError={() => setHasThumbnailFailed(true)}
          />
        )}

        {(isWatched || coveredPercent > 0) && (
          <span
            className="module-video-strip"
            style={{ width: `${isWatched ? 100 : coveredPercent}%` }}
          />
        )}
      </div>

      <div className="flex flex-col gap-100">
        <span
          className={`body-xs-regular ${isWatched ? "module-video-watched" : "certification-muted"}`}
        >
          {status}
        </span>

        <h3 className="certification-heading body-m-medium">
          {t("certification.module.videos.title", {
            number,
            title: video.title,
          })}
        </h3>
      </div>

      <div>
        <BaseButton
          variant={
            isWatched ? BUTTON_VARIANTS.TERTIARY : BUTTON_VARIANTS.PRIMARY
          }
          size={BUTTON_SIZES.SMALL}
          ariaLabel={t(
            isWatched
              ? "certification.module.videos.rewatchLabel"
              : "certification.module.videos.watchLabel",
            { title: video.title },
          )}
          onClick={() => onPlay({ video, number })}
        >
          {isWatched
            ? t("certification.module.videos.rewatch")
            : t("certification.module.videos.watch")}
        </BaseButton>
      </div>
    </article>
  );
};

export default VideoCard;
